import * as XLSX from 'xlsx';

export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
};

const formatTransferSheetName = (kec: string, shortDate: string) => {
  if (!shortDate || shortDate.length < 6) return kec;

  const dd = shortDate.substring(0, 2);
  const mm = shortDate.substring(2, 4);
  const yy = shortDate.substring(4, 6);

  const months = [
    "JAN", "FEB", "MAR", "APR", "MEI", "JUN",
    "JUL", "AGU", "SEP", "OKT", "NOV", "DES"
  ];

  const monthName = months[Number(mm) - 1] || mm;

  return `${kec} - ${dd} ${monthName} ${yy}`;
};

export const formatDateSQL = (dateString: string) => {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // bulan mulai dari 0
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type TransformAndGroupOptions = {
  filterKls?: string | number | null;
  filterBln?: string | number | null;
  filterThn?: string | number | null;
};

export const TableViewRekapLpp = (data: any[] = [], opts: TransformAndGroupOptions = {}) => {
  const inputData = Array.isArray(data) ? data : [];
  const { filterKls = null, filterBln = null, filterThn = null } = opts;
 
  // Helper Functions
  const safeNumber = (v: unknown) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return v;
    const cleaned = String(v).replace(/[^0-9.-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  const toDecimal = (v: unknown) => safeNumber(v).toLocaleString('id-ID', { style: 'decimal' });
 
  const extractKecamatan = (alamat: string) => {
    if (!alamat) return "TIDAK TERDEFINISI";
    const match = alamat.match(/KEC(?:AMATAN)?(?:\.|\s+)\s*(.*?)\s+DESA/i);
    return match ? match[1].trim().toUpperCase() : alamat;
  };
 
  // 1) Filter & Sort — ✅ Disamakan dengan ExportFilterRekapLpp
  // - Baca PERIODE dari field PERIODE (uppercase) dengan fallback ke periode (lowercase)
  // - Sort: PERIODE ASC, lalu KLASF ASC
  const filtered = inputData
    .filter(item => {
      if (!item) return false;
      const pRaw = String(item.PERIODE || item.periode || "");
      if (!pRaw || pRaw.length < 6) return false;
 
      const itemYear  = pRaw.substring(0, 4);
      const itemMonth = pRaw.substring(4, 6);
 
      if (filterThn && itemYear  !== String(filterThn))                    return false;
      if (filterBln && itemMonth !== String(filterBln).padStart(2, '0'))   return false;
      if (filterKls && String(item.KLASF) !== String(filterKls))           return false;
      return true;
    })
    .sort((a, b) => {
      const pA = String(a.PERIODE || a.periode || "");
      const pB = String(b.PERIODE || b.periode || "");
      if (pA !== pB) return pA.localeCompare(pB);
 
      const kA = String(a.KLASF || "");
      const kB = String(b.KLASF || "");
      return kA.localeCompare(kB);
    });

  const nestedGroups: Record<string, Record<string, any[]>> = {};
  const grandTotalsRaw = { hair: 0, adm: 0, jml: 0, dnd: 0, ttl: 0 };
 
  filtered.forEach((item) => {
    const kec      = extractKecamatan(item.ALAMAT);
    const rawKey   = `${item.PERIODE || item.periode || ""} - LPP ${item.KLASF || ""}`;
    const uniqueId = `${item.NOSAMW}-${item.periode}`;
 
    if (!nestedGroups[kec])         nestedGroups[kec] = {};
    if (!nestedGroups[kec][rawKey]) nestedGroups[kec][rawKey] = [];
 
    const hair = safeNumber(item.R1) + safeNumber(item.R2) + safeNumber(item.R3) + safeNumber(item.R4);
    const adm  = safeNumber(item.ADM);
    const dnd  = safeNumber(item.DENDA);
    const jml  = hair + adm;
    const ttl  = jml + dnd;
 
    nestedGroups[kec][rawKey].push({
      ...item,
      calc: { hair, adm, jml, dnd, ttl, uniqueId }
    });
 
    grandTotalsRaw.hair += hair;
    grandTotalsRaw.adm  += adm;
    grandTotalsRaw.jml  += jml;
    grandTotalsRaw.dnd  += dnd;
    grandTotalsRaw.ttl  += ttl;
  });
 
  const tableRows: any[] = [];
  let globalCounter = 1;
 
  Object.keys(nestedGroups).sort().forEach(kec => {
    let grandKecHair = 0, grandKecAdm = 0, grandKecJml = 0, grandKecDnd = 0, grandKecTtl = 0;
 
    Object.keys(nestedGroups[kec]).sort().forEach(rawKey => {
      const currentGroupData = nestedGroups[kec][rawKey];
      let subHair = 0, subAdm = 0, subJml = 0, subDnd = 0, subTtl = 0;
 
      tableRows.push({ is_group_header: true, label: `${kec} | ${rawKey}` });
 
      currentGroupData.forEach((item) => {
        const c = item.calc;
        subHair += c.hair; subAdm += c.adm; subJml += c.jml; subDnd += c.dnd; subTtl += c.ttl;
        grandKecHair += c.hair; grandKecAdm += c.adm; grandKecJml += c.jml; grandKecDnd += c.dnd; grandKecTtl += c.ttl;
 
        const ket        = extractKecamatan(item.ALAMAT);
        const periodeFmt = item.periode
          ? `${item.periode.substring(0, 4)}-${item.periode.substring(4, 6)}`
          : "";
        const plggnFmt   =
          item.stat_smb === '30' ? 'Aktif'       :
          item.stat_smb === '40' ? 'Segel'        :
          item.stat_smb === '20' ? 'Cabut Meter'  :
          item.stat_smb;
 
        tableRows.push({
          no:             globalCounter++,
          periode:        periodeFmt,
          nsb:            item.NOSAMW  || "",
          nama:           item.NAMA    || "",
          kls:            item.KLASF   || "",
          plggn:          plggnFmt,
          hair:           toDecimal(c.hair),
          adm:            toDecimal(c.adm),
          jml:            toDecimal(c.jml),
          dnd:            toDecimal(c.dnd),
          ttl:            toDecimal(c.ttl),
          ket:            ket,
          is_summary:     false,
          is_group_header: false,
          uniqueId:       c.uniqueId
        });
      });
 
      if (currentGroupData.length > 1) {
        tableRows.push({
          no: "", periode: "", nsb: "", nama: "", kls: "", plggn: "", ket: "",
          hair: toDecimal(subHair),
          adm:  toDecimal(subAdm),
          jml:  toDecimal(subJml),
          dnd:  toDecimal(subDnd),
          ttl:  toDecimal(subTtl),
          is_summary: true
        });
      }
    });
  });
 
  return {
    rows: tableRows,
    totals: {
      raw: grandTotalsRaw,
      formatted: {
        shair: toDecimal(grandTotalsRaw.hair),
        sadm:  toDecimal(grandTotalsRaw.adm),
        sjml:  toDecimal(grandTotalsRaw.jml),
        sdnd:  toDecimal(grandTotalsRaw.dnd),
        sttl:  toDecimal(grandTotalsRaw.ttl)
      }
    }
  };
};

export const ExportFilterRekapLpp = (data: any[] = [], opts: any = {}) => {
  const inputData = Array.isArray(data) ? data : [];
  const {
    filterKls = null,
    filterBln = null,
    filterThn = null,
    checkedTransfers = {} as Record<string, string>, // ✅ Ubah dari array ke object
    isExport = false
  } = opts;
 
  const toDecimal = (v: any) => Number(v || 0).toLocaleString('id-ID');
 
  const extractKecamatan = (alamat: string) => {
    if (!alamat) return "TIDAK TERDEFINISI";
    const match = alamat.match(/KEC(?:AMATAN)?(?:\.|\s+)\s*(.*?)\s+DESA/i);
    return match ? match[1].trim().toUpperCase() : alamat;
  };
 
  const filtered = inputData
    .filter(item => {
      const pRaw = String(item.PERIODE || item.periode || "");
      if (!pRaw || pRaw.length < 6) return false;
 
      const itemYear  = pRaw.substring(0, 4);
      const itemMonth = pRaw.substring(4, 6);
 
      if (filterThn && itemYear  !== String(filterThn))                  return false;
      if (filterBln && itemMonth !== String(filterBln).padStart(2, '0')) return false;
      if (filterKls && String(item.KLASF) !== String(filterKls))         return false;
 
      return true;
    })
    .sort((a, b) => {
      const pA = String(a.PERIODE || a.periode || "");
      const pB = String(b.PERIODE || b.periode || "");
      if (pA !== pB) return pA.localeCompare(pB);
 
      const kA = String(a.KLASF || "");
      const kB = String(b.KLASF || "");
      return kA.localeCompare(kB);
    });
 
  const nestedGroups: Record<string, Record<string, any[]>> = {};
  const grandTotalsRaw = { hair: 0, adm: 0, dnd: 0, ttl: 0 };
 
  filtered.forEach((item) => {
    let kec = extractKecamatan(item.ALAMAT);
    const rawKey   = `${item.PERIODE || item.periode || ""} - LPP ${item.KLASF || ""}`;
    const uniqueId = `${item.NOSAMW}-${item.periode}`;
 
    // ✅ Cek apakah uniqueId ada di checkedTransfers (object)
    const transferDate = isExport ? (checkedTransfers[uniqueId] ?? null) : null;
 
    if (transferDate) {
      kec = formatTransferSheetName(kec, transferDate);
    }
 
    if (!nestedGroups[kec])         nestedGroups[kec] = {};
    if (!nestedGroups[kec][rawKey]) nestedGroups[kec][rawKey] = [];
 
    const hair = Number(item.R1 || 0) + Number(item.R2 || 0) + Number(item.R3 || 0) + Number(item.R4 || 0);
    const adm  = Number(item.ADM   || 0);
    const dnd  = Number(item.DENDA || 0);
    const ttl  = hair + adm + dnd;
 
    nestedGroups[kec][rawKey].push({
      ...item,
      calc: { hair, adm, jml: hair + adm, dnd, ttl, kec, rawKey, uniqueId, transferDate }
    });
 
    grandTotalsRaw.hair += hair;
    grandTotalsRaw.adm  += adm;
    grandTotalsRaw.dnd  += dnd;
    grandTotalsRaw.ttl  += ttl;
  });
 
  const tableRows: any[] = [];
  const excelSheetsData: Record<string, any[]> = {};
 
  Object.keys(nestedGroups).sort().forEach(kec => {
    excelSheetsData[kec] = [];
 
    let sheetCounter = 1;
    let grandKecHair = 0, grandKecAdm = 0, grandKecDnd = 0, grandKecTtl = 0;
 
    Object.keys(nestedGroups[kec]).sort().forEach(rawKey => {
      const currentGroupData = nestedGroups[kec][rawKey];
      let subHair = 0, subAdm = 0, subDnd = 0, subTtl = 0;
 
      tableRows.push({ is_group_header: true, label: `${kec} | ${rawKey}` });
 
      currentGroupData.forEach((item) => {
        const c = item.calc;
        subHair += c.hair; subAdm += c.adm; subDnd += c.dnd; subTtl += c.ttl;
        grandKecHair += c.hair; grandKecAdm += c.adm; grandKecDnd += c.dnd; grandKecTtl += c.ttl;
 
        const currentNo = sheetCounter++;
        const ketBase   = item.ALAMAT.match(/KEC(?:AMATAN)?(?:\.|\s+)\s*(.*?)\s+DESA/i)?.[1] || item.ALAMAT;
 
        // Kolom ALAMAT di Excel: tetap ketBase saja, tanggal transfer hanya dipakai untuk nama sheet
        const ketExcel = ketBase;
 
        tableRows.push({
          no:              currentNo,
          periode:         item.periode,
          nsb:             item.NOSAMW,
          nama:            item.NAMA,
          kls:             item.KLASF,
          plggn:           item.stat_smb === '30' ? 'Aktif'
                         : item.stat_smb === '40' ? 'Segel'
                         : item.stat_smb === '20' ? 'Cabut Meter'
                         : item.stat_smb,
          hair:            toDecimal(c.hair),
          adm:             toDecimal(c.adm),
          jml:             toDecimal(c.jml),
          dnd:             toDecimal(c.dnd),
          ttl:             toDecimal(c.ttl),
          ket:             ketExcel,
          is_summary:      false,
          is_group_header: false,
          uniqueId:        c.uniqueId
        });
 
        excelSheetsData[kec].push({
          "NO":      currentNo,
          "PERIODE": item.periode,
          "NOSAMW":  item.NOSAMW,
          "NAMA":    item.NAMA,
          "KLASF":   item.KLASF,
          "STATUS":  item.stat_smb === '30' ? 'Aktif'
                   : item.stat_smb === '40' ? 'Segel'
                   : item.stat_smb === '20' ? 'Cabut Meter'
                   : item.stat_smb,
          "HAIR":    c.hair,
          "ADM":     c.adm,
          "JUMLAH":  c.jml,
          "DENDA":   c.dnd,
          "TOTAL":   c.ttl,
          "ALAMAT":  ketExcel
        });
      });
 
      if (currentGroupData.length > 1) {
        tableRows.push({
          nama:            ``,
          hair:            toDecimal(subHair),
          adm:             toDecimal(subAdm),
          jml:             toDecimal(subHair + subAdm),
          dnd:             toDecimal(subDnd),
          ttl:             toDecimal(subTtl),
          is_summary:      true
        });
 
        excelSheetsData[kec].push({
          "NO": "", "PERIODE": "", "NOSAMW": "", "NAMA": "", "KLASF": "", "STATUS": "",
          "HAIR": subHair, "ADM": subAdm, "JUMLAH": subHair + subAdm, "DENDA": subDnd, "TOTAL": subTtl, "ALAMAT": ""
        });
      }
 
      excelSheetsData[kec].push({ "NO": "", "PERIODE": "", "NOSAMW": "", "NAMA": "" });
    });
 
    excelSheetsData[kec].push({
      "NO":      "",
      "PERIODE": "",
      "NOSAMW":  "",
      "NAMA":    `TOTAL KECAMATAN ${kec}`,
      "KLASF":   "",
      "STATUS":  "",
      "HAIR":    grandKecHair,
      "ADM":     grandKecAdm,
      "JUMLAH":  grandKecHair + grandKecAdm,
      "DENDA":   grandKecDnd,
      "TOTAL":   grandKecTtl,
      "ALAMAT":  ""
    });
  });
 
  return {
    rows:             tableRows,
    dataPerKecamatan: excelSheetsData,
    totals:           { /* ... */ }
  };
};

export const handleDownloadExcel = (filteredLppDetail: any[], currentFilters: any, kode_byr : string) => {
    const { dataPerKecamatan } = ExportFilterRekapLpp(filteredLppDetail, { 
        ...currentFilters,
        isExport: true 
    });

    const wb = XLSX.utils.book_new();

    Object.keys(dataPerKecamatan).sort().forEach(kecName => {
        const excelRows = dataPerKecamatan[kecName];
        
        const ws = XLSX.utils.json_to_sheet(excelRows);
        XLSX.utils.book_append_sheet(wb, ws, kecName.substring(0, 31));
    });
    XLSX.writeFile(wb, `LPP_Per_Kecamatan_${kode_byr}_${new Date().getTime()}.xlsx`);

    return dataPerKecamatan;
    console.log(dataPerKecamatan);



    // 
};